// 📄 hooks/useAllChannelsProgress.ts
// Progreso de los canales de integración de Commerce (Orden de Compra,
// Aviso de Expedición, Confirmación, Recepción de Mercancía, HES, HES-ERP,
// Facturas, Estatus de Facturas) para las cards de CommerceHome.tsx.
//
// Una sola lectura de Firestore (getGeneralsConfig) en vez de N lecturas
// por canal: reutiliza environments.DEV.checklistProgress, la misma fuente
// que useOrdenCompra.ts / useAvisoExpedicion.ts / etc. usan para calcular
// "Pasos completados X de Y" en cada página de canal.
//
// El progreso se devuelve indexado por el `id` de COMMERCE_CHECKLISTS
// (commerceChecklistCatalog.ts) — esa es la única lista de "qué canales
// tiene Commerce" en todo el código; antes existía una segunda copia acá
// (CHANNEL_CARD_KEYS) que traducía las keys de onSelect() de
// CommerceHome.tsx a estos mismos ids. Se eliminó: CommerceHome.tsx ahora
// usa los ids del catálogo directamente para leer su progreso.
//
// No incluye "generals" (no es un canal, es transversal) ni "cutover" (su
// progreso vive en environments.{env}.cutover.completedTasks, no en
// checklistProgress, y hoy no tiene card en CommerceHome.tsx).
import { useEffect, useState } from 'react';
import { getGeneralsConfig } from '../services/commerce/integrationService.ts';
import { COMMERCE_CHECKLISTS } from '../data/commerceChecklistCatalog.ts';

const EXCLUDED_CATALOG_IDS = new Set(['generals', 'cutover']);

export const COMMERCE_CHANNELS = COMMERCE_CHECKLISTS.filter((c) => !EXCLUDED_CATALOG_IDS.has(c.id));

export interface ChannelProgress {
  completed: number;
  total: number;
  lastCompleted: string | null;
  contextText: string;
}

const stripStepNumber = (title: string): string => title.replace(/^\d+\.\s*/, '');

function getContextText(completed: number, total: number, lastCompleted: string | null): string {
  if (total === 0) return 'Sin pasos definidos';
  if (completed === 0) return 'Sin configurar';
  if (completed >= total) return 'Completado';
  return `${lastCompleted} completado`;
}

export function useAllChannelsProgress(projectId: string) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, ChannelProgress>>({});

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    let cancelado = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const commerceIntegration = await getGeneralsConfig(projectId);
        if (cancelado) return;

        const checklistProgress: Record<string, boolean> = commerceIntegration?.environments?.DEV?.checklistProgress || {};

        const result: Record<string, ChannelProgress> = {};
        COMMERCE_CHANNELS.forEach((canal) => {
          const steps = canal.steps || [];
          const completedSteps = steps.filter((s) => checklistProgress[s.id] === true);
          const total = steps.length;
          const completed = completedSteps.length;
          const lastCompleted = completedSteps.length > 0
            ? stripStepNumber(completedSteps[completedSteps.length - 1].title)
            : null;

          result[canal.id] = { completed, total, lastCompleted, contextText: getContextText(completed, total, lastCompleted) };
        });

        setProgress(result);
      } catch (err) {
        console.error('Error cargando progreso de canales:', err);
        if (!cancelado) setError('No se pudo cargar el progreso de los canales.');
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [projectId]);

  return { loading, error, progress };
}
